import { useRef, useState, useEffect, useCallback } from 'react';
import { Icon } from '../../primitives';

/**
 * VideoPlayer — inline video with timeline, play/pause toggle, timecodes.
 *
 * Figma source: Video viewer `319:5793` — video-player INSTANCE (from `321:5856`).
 *
 * Layout:
 *  - <video loop autoPlay playsInline> fills the container
 *  - Timeline bar 370×16: bg-white/16 (full-height track), bg-white/48
 *    (progress fill) — both span the full 16px tap-target, no thumb circle
 *    (Figma `321:5856` video-player INSTANCE).
 *  - Play/pause and sound icons 24×24 (bottom-left of controls row)
 *  - Timecodes: "0:00" left / total-duration right, Raleway SemiBold 12sp #C9C9C9
 *
 * Audio: enabled in the opened gallery viewer. If a browser blocks autoplay with
 * sound, the user can start playback or toggle sound from the controls.
 *
 * visibilitychange: pause when hidden, resume when visible (if was playing).
 *
 * Props:
 *  src         (string)   video URL
 *  className?  (string)
 *  referenceImageUrls? (string[]) source references shown above the player row
 */
export default function VideoPlayer({
  src,
  className = '',
  referenceImageUrls = [],
}) {
  const videoRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [tapFeedback, setTapFeedback] = useState(null);
  const wasPausedBeforeHide = useRef(false);

  // Format seconds → "m:ss"
  const fmt = (s) => {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause
  const toggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, []);

  const showTapFeedback = useCallback((action) => {
    window.clearTimeout(feedbackTimerRef.current);
    setTapFeedback({
      action,
      key: Date.now(),
    });
    feedbackTimerRef.current = window.setTimeout(() => {
      setTapFeedback(null);
    }, 520);
  }, []);

  const handleSurfaceClick = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      showTapFeedback('play');
      v.play().catch(() => {});
    } else {
      showTapFeedback('pause');
      v.pause();
    }
  }, [showTapFeedback]);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    const nextMuted = !v.muted;
    v.muted = nextMuted;
    if (!nextMuted) v.volume = 1;
    setIsMuted(nextMuted);
  }, []);

  // Sync state from video events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = false;
    v.volume = 1;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      if (!isDragging) setCurrentTime(v.currentTime);
    };
    const onLoadedMetadata = () => setDuration(v.duration || 0);
    const onEnded = () => setIsPlaying(false);

    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('loadedmetadata', onLoadedMetadata);
    v.addEventListener('ended', onEnded);

    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('loadedmetadata', onLoadedMetadata);
      v.removeEventListener('ended', onEnded);
    };
  }, [isDragging]);

  // visibilitychange: pause when hidden, resume when visible (if was playing)
  useEffect(() => {
    const handleVisibility = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.visibilityState === 'hidden') {
        wasPausedBeforeHide.current = v.paused;
        if (!v.paused) {
          v.pause();
        }
      } else if (document.visibilityState === 'visible') {
        if (!wasPausedBeforeHide.current) {
          v.play().catch(() => {});
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => () => {
    window.clearTimeout(feedbackTimerRef.current);
  }, []);

  // Timeline click → seek
  const handleTimelineClick = useCallback((e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  }, [duration]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`h-full w-full ${className}`} data-testid="video-player">
      {/* Video element */}
      <video
        ref={videoRef}
        src={src}
        muted={isMuted}
        loop
        autoPlay
        playsInline
        data-testid="video-element"
        className="h-full w-full object-cover object-center"
        style={{ display: 'block' }}
      />

      <button
        type="button"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        className="absolute inset-0 z-0 cursor-default bg-transparent"
        onClick={handleSurfaceClick}
        data-testid="video-surface"
      />

      {tapFeedback && (
        <div
          key={tapFeedback.key}
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
          data-testid="video-tap-feedback"
        >
          <div
            className="video-tap-feedback flex h-16 w-16 items-center justify-center rounded-full bg-black/40 text-white shadow-lg backdrop-blur-sm"
            data-testid={`video-tap-feedback-${tapFeedback.action}`}
          >
            <Icon name={tapFeedback.action} size={32} className="text-white" />
          </div>
        </div>
      )}

      {/* Controls overlay — bottom of video container */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-3 pt-8"
        style={{
          background:
            'linear-gradient(0deg, rgba(1,1,3,0.72) 0%, rgba(1,1,3,0) 100%)',
        }}
        data-testid="video-controls"
      >
        {referenceImageUrls.length > 0 && (
          <div
            className="pointer-events-none mb-3 flex gap-2"
            data-testid="reference-thumbnails"
          >
            {referenceImageUrls.slice(0, 2).map((url, index) => (
              <img
                key={url}
                src={url}
                alt=""
                className="rounded-home-8 object-cover"
                style={{
                  width: 42,
                  height: 42,
                  border: '1px solid rgba(255, 255, 255, 0.32)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.56)',
                }}
                data-testid={`reference-thumbnail-${index}`}
              />
            ))}
          </div>
        )}

        {/* Play/pause + timecodes row */}
        <div className="flex items-center gap-3" data-testid="video-controls-row">
          <button
            type="button"
            onClick={toggle}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="flex items-center justify-center text-text-primary flex-shrink-0"
            data-testid="play-pause-btn"
          >
            <Icon
              name={isPlaying ? 'pause' : 'play'}
              size={24}
              className="text-text-primary"
            />
          </button>

          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            className="flex items-center justify-center text-text-primary flex-shrink-0"
            data-testid="mute-toggle-btn"
          >
            <Icon
              name={isMuted ? 'sound-off' : 'sound-on'}
              size={24}
              className="text-text-primary"
            />
          </button>

          {/* Current time */}
          <span
            className="font-sans font-semibold text-text-secondary flex-shrink-0"
            style={{ fontSize: 12, lineHeight: '14.088px' }}
            data-testid="timecode-current"
          >
            {fmt(currentTime)}
          </span>

          {/* Timeline */}
          <div
            className="flex-1 relative cursor-pointer"
            style={{ height: 16 }}
            onClick={handleTimelineClick}
            data-testid="timeline"
          >
            {/* Track — bg-glass-16 token, full 16px per Figma `321:5856` */}
            <div
              className="absolute inset-y-0 w-full rounded-full bg-glass-16"
              style={{ top: '50%', transform: 'translateY(-50%)', height: 16 }}
            />
            {/* Progress — #fff@48; no glass-48 token → figma:ref rgba(255,255,255,0.48) */}
            <div
              className="absolute rounded-full"
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: `${progress}%`,
                backgroundColor: 'rgba(255,255,255,0.48)', // figma:ref #fff@48 timeline progress
                transition: isDragging ? 'none' : 'width 0.1s linear',
              }}
              data-testid="timeline-progress"
            />
          </div>

          {/* Total duration */}
          <span
            className="font-sans font-semibold text-text-secondary flex-shrink-0"
            style={{ fontSize: 12, lineHeight: '14.088px' }}
            data-testid="timecode-duration"
          >
            {fmt(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}
